import { computed, inject, Injectable, signal } from "@angular/core";
import { catchError, map, Observable, of, tap } from "rxjs";
import { AuthService } from "@app/core/services/auth.service";
import { RoleService } from "@app/shared/services/role.service";
import { EnrichmentChannelDirectory } from "../enrichment-channel-directory.service";
import {
  ChannelLinkOutcome,
  LinkChannel,
  LinkRole,
  resolveChannelLink,
} from "./channel-link.util";

/**
 * A link's `?channel=`, read against the channels there are and the roles
 * this user holds — and the same things the other way round, for writing
 * the channel into a link.
 */
@Injectable({ providedIn: "root" })
export class ChannelLinkService {
  private readonly roles = inject(RoleService);
  private readonly auth = inject(AuthService);
  private readonly directory = inject(EnrichmentChannelDirectory);
  /** False until the channel list has answered, and when it could not. */
  private readonly directoryLoaded = signal(false);

  /** Completes once the channel list was asked for, read or not: a link is
   *  still judged by the user's roles when the list cannot be read. */
  ready(): Observable<void> {
    return this.directory.load().pipe(
      tap(() => this.directoryLoaded.set(true)),
      map(() => undefined),
      catchError(() => {
        this.directoryLoaded.set(false);
        return of(undefined);
      }),
    );
  }

  /** What `?channel=<value>` comes to for the role on screen. */
  resolve(value: string | null | undefined): ChannelLinkOutcome {
    return resolveChannelLink({
      value,
      directory: this.directory.channels(),
      directoryLoaded: this.directoryLoaded(),
      profiles: this.roles.availableRoleProfiles(),
      selectedRoleId: this.roles.selectedRole()?.roleId ?? null,
    });
  }

  /** The channel the role on screen is bound to; null for a role on all
   *  channels, or none. Its key comes from the channel list where that
   *  knows the channel — a role's own channel carries a name only. */
  readonly boundChannel = computed<LinkChannel | null>(() => {
    if (this.roles.isApplicationOwner()) return null;
    const channel = this.roles.activeChannel();
    if (!channel) return null;
    const row = this.directory
      .channels()
      .find((c) => c.channelId === channel.channelId);
    return {
      key:
        row?.value?.trim() || channel.channelKey?.trim() || channel.channelName,
      name: row?.label?.trim() || channel.channelName,
      channelId: channel.channelId,
    };
  });

  /** The bound role's channel as the screens send it in `x-channel`: its
   *  key when the role payload carries one, else its sale name (the
   *  Enrichment list's `roleChannelValue`). "" for a role on all channels. */
  roleChannelValue(): string {
    if (this.roles.isApplicationOwner()) return "";
    const channel = this.roles.activeChannel();
    return channel?.channelKey?.trim() || channel?.channelName?.trim() || "";
  }

  /** How a channel a screen works on reads ("myais" → "myAIS"); the value
   *  itself for one the list does not know, "" for none. */
  channelName(value: string): string {
    return value ? this.directory.getLabel(value) : "";
  }

  /** How the role on screen reads: "Channel Owner · myAIS". */
  roleLabel(): string {
    const role = this.roles.selectedRole()?.roleName ?? "";
    const channel = this.roles.isApplicationOwner()
      ? "All channels"
      : (this.roles.activeChannelName() ?? "");
    return [role, channel].filter(Boolean).join(" · ");
  }

  /** Activate the role a link needs. The header and every screen follow,
   *  as after a switch from the header. */
  switchRole(role: LinkRole): Observable<void> {
    return this.auth.activateRole(role.roleId, role.channelId).pipe(
      tap(() => this.roles.applyRoleChannel(role.roleId, role.channelId)),
      map(() => undefined),
    );
  }
}
